import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface GoogleAdProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'horizontal' | 'vertical';
  className?: string;
}

export function GoogleAd({ adSlot, adFormat = 'auto', className = '' }: GoogleAdProps) {
  const { profile, entitlement } = useAuth();
  const adRef = useRef<HTMLDivElement>(null);
  const isLoaded = useRef(false);

  // Check if user should see ads
  const shouldShowAds = !profile?.is_verified && !entitlement?.remove_ads;

  useEffect(() => {
    if (!shouldShowAds || isLoaded.current) return;

    const adClient = import.meta.env.VITE_ADSENSE_CLIENT;
    if (!adClient) {
      console.warn('AdSense: No client ID configured');
      return;
    }

    try {
      // Push ad request
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      isLoaded.current = true;
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, [shouldShowAds]);

  // Don't render anything for verified/paid users
  if (!shouldShowAds) {
    return null;
  }

  const adClient = import.meta.env.VITE_ADSENSE_CLIENT;
  if (!adClient) {
    // Show placeholder if no AdSense configured
    return (
      <div className={`ad-placeholder ${className}`}>
        <p className="text-sm font-medium mb-1">Advertisement</p>
        <p className="text-xs opacity-70">AdSense pending configuration</p>
      </div>
    );
  }

  return (
    <div ref={adRef} className={className}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
    </div>
  );
}
